import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { useSurveys } from '../contexts/SurveyContext';

const Survey: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { surveys, loading } = useSurveys();
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredSurveys = surveys.filter(survey =>
    survey.title.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 로그인 가드
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1920&h=600&fit=crop&q=80"
              alt="Survey"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">참여마당</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">설문조사</h1>
            <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
          </div>
        </div>
        <div className="flex-1 py-8 sm:py-10 md:py-12 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-16 sm:py-20">
              <p className="text-gray-500 text-base sm:text-lg mb-4">로그인 후 이용할 수 있는 서비스입니다.</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                로그인
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="설문조사"
        description="동두천시 글로벌인재교육센터의 설문조사에 참여하세요."
        keywords="설문조사, 동두천글로벌인재교육센터"
        path="/community/survey"
      />
      <div className="min-h-screen flex flex-col">
        <Header />

        <main className="flex-1 pt-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 pt-16 pb-12">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">설문조사</h1>
              <p className="text-gray-600">수업별 설문조사에 참여해주세요.</p>
            </div>

            {/* Search */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="검색어를 입력하세요"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Survey List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        번호
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        제목
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        작성일
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSurveys.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                          등록된 설문조사가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      filteredSurveys.map((survey, index) => (
                        <tr key={survey.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/community/survey/${survey.id}`}
                              className="text-sm text-gray-900 hover:text-blue-600 font-medium"
                            >
                              {survey.title}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(survey.created_at).toLocaleDateString('ko-KR')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Survey;
